import { useState } from "react";
import { useAtomValue } from "jotai";
import { Brand } from "../../../../gql/__generated__/graphql";
import { GET_ALL_BRANDS } from "../../../apis/lambda/GraphQL/Brand/queries";
import { setupCompleteAtom } from "../../../atoms";
import { BrandCard } from "../../../components/Cards/BrandCard";
import {
  FilterOption,
  SortOption,
} from "../../../components/Filter/FilterPanel";
import { DiscoverBaseComponent } from "../DiscoverBaseComponent";

export function DiscoverBrands() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [minProducts, setMinProducts] = useState<number | "">("");
  const [minRating, setMinRating] = useState(0);
  const setupComplete = useAtomValue(setupCompleteAtom);

  const filterOptions: FilterOption[] = [
    {
      id: "search",
      label: "Search",
      type: "search",
      placeholder: "Search brands by name or description",
      value: searchTerm,
      onChange: setSearchTerm,
      searchFields: ["name", "description"],
    },
    {
      id: "categories",
      label: "Categories",
      type: "multiSelect",
      placeholder: "Select categories",
      data: [
        { value: "Beauty", label: "Beauty" },
        { value: "Organic", label: "Organic" },
        { value: "Skincare", label: "Skincare" },
        { value: "Fashion", label: "Fashion" },
        { value: "Food", label: "Food" },
        { value: "Health", label: "Health" },
      ],
      value: categories,
      onChange: setCategories,
      filterFn: (brand: Brand, selectedCategories: string[]) => {
        if (selectedCategories.length === 0) return true;
        if (!brand.categories) return true;
        return selectedCategories.some((category) =>
          brand.categories?.includes(category)
        );
      },
    },
    {
      id: "minProducts",
      label: "Min Products",
      type: "numberInput",
      placeholder: "e.g. 10",
      suffix: "+",
      value: minProducts,
      onChange: setMinProducts,
      filterFn: (brand: Brand, minValue: number | "") => {
        if (minValue === "") return true;
        return (brand.productsCount || 0) >= minValue;
      },
    },
    {
      id: "minRating",
      label: "Minimum Rating",
      type: "slider",
      min: 0,
      max: 5,
      step: 0.1,
      marks: [
        { value: 0, label: "0" },
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
        { value: 4, label: "4" },
        { value: 5, label: "5" },
      ],
      value: minRating,
      onChange: setMinRating,
      field: "rating",
    },
  ];

  const sortOptions: SortOption[] = [
    {
      value: "rating",
      label: "Highest Rating",
      sortFn: (a: Brand, b: Brand) => (b.rating ?? 0) - (a.rating ?? 0),
    },
    {
      value: "products",
      label: "Most Products",
      sortFn: (a: Brand, b: Brand) =>
        (b.productsCount || 0) - (a.productsCount || 0),
    },
    {
      value: "name",
      label: "Name (A-Z)",
      sortFn: (a: Brand, b: Brand) =>
        (a.name ?? "").localeCompare(b.name ?? ""),
    },
  ];

  return (
    <DiscoverBaseComponent<Brand>
      title="Discover Brands"
      subtitle="Find the perfect brand to partner with"
      query={GET_ALL_BRANDS}
      dataKey="brands"
      filterOptions={filterOptions}
      sortOptions={sortOptions}
      renderCard={(brand) => <BrandCard brand={brand} setupComplete={setupComplete} />}
      initialSortBy="rating"
      filterValidItems={(brand) => (brand.productsCount || 0) > 0}
    />
  );
}
